import React from 'react';
import { Badge, Note, Progress, Spinner, Sym } from '../../../components/app2';
import {
  contarRecibos, enCastellano, fechaHora, periodoDe,
} from '../../../utils/syncFormat';

/**
 * Estado de la sincronización, contado como una historia de cuatro pasos.
 *
 * Desde jul-2026 lee el estado del ORQUESTADOR del servidor, no códigos crudos
 * del SAT. Antes traducía `sat_status_code` y cualquier valor inesperado caía en
 * "Estado desconocido · Código 404", que asustaba sin motivo: el proceso iba
 * bien, solo que el SAT había contestado algo intermedio.
 */

const FASES = [
  { id: 'auth', num: 1, title: 'Autenticar', icon: 'key', desc: 'Con la e.firma de la empresa' },
  { id: 'query', num: 2, title: 'Consultar', icon: 'send', desc: 'Se pide el periodo al SAT' },
  { id: 'verify', num: 3, title: 'Verificar', icon: 'hourglass_top', desc: 'El SAT prepara los paquetes' },
  { id: 'download', num: 4, title: 'Descargar', icon: 'cloud_download', desc: 'Se bajan y procesan los recibos' },
];

/** En qué va cada fase según el estado del trabajo en el servidor. */
const fasesSegun = (status) => {
  switch (status) {
    case 'encolada':
      return { auth: 'pending', query: 'pending', verify: 'pending', download: 'pending' };
    case 'solicitud_aceptada':
    case 'en_proceso':
      return { auth: 'done', query: 'done', verify: 'active', download: 'pending' };
    case 'lista_para_descargar':
      return { auth: 'done', query: 'done', verify: 'done', download: 'active' };
    case 'descargando':
      return { auth: 'done', query: 'done', verify: 'done', download: 'active' };
    case 'terminada':
      return { auth: 'done', query: 'done', verify: 'done', download: 'done' };
    case 'failed':
    case 'rejected':
      return { auth: 'done', query: 'failed', verify: 'pending', download: 'pending' };
    case 'expirada':
      return { auth: 'done', query: 'done', verify: 'done', download: 'failed' };
    case 'cancelada':
      return { auth: 'pending', query: 'pending', verify: 'pending', download: 'pending' };
    default:
      return { auth: 'pending', query: 'pending', verify: 'pending', download: 'pending' };
  }
};

const AVANCE = {
  encolada: 10,
  solicitud_aceptada: 35,
  en_proceso: 55,
  lista_para_descargar: 80,
  descargando: 92,
  terminada: 100,
  failed: 100,
  rejected: 100,
  expirada: 100,
  cancelada: 100,
};

const resumen = (solicitud) => {
  if (!solicitud) {
    return { tone: 'info', icon: 'cloud_off', title: 'Sin sincronización', message: 'Elige un periodo y comienza.' };
  }
  switch (solicitud.status) {
    case 'encolada':
      return { tone: 'brand', icon: 'schedule', title: 'En espera', message: 'El servidor la tomará en unos segundos.', spinner: true };
    case 'solicitud_aceptada':
      return { tone: 'brand', icon: 'send', title: 'Pedida al SAT', message: 'El SAT recibió la solicitud y la puso en su cola.', spinner: true };
    case 'en_proceso':
      return { tone: 'brand', icon: 'autorenew', title: 'El SAT la está preparando', message: 'Puede tardar de segundos a varias horas; no hace falta esperar aquí.', spinner: true };
    case 'lista_para_descargar':
      return { tone: 'warn', icon: 'cloud_download', title: 'Lista para descargar', message: 'El servidor está bajando los paquetes.', spinner: true };
    case 'descargando':
      return { tone: 'brand', icon: 'downloading', title: 'Descargando', message: 'Bajando y procesando los recibos.', spinner: true };
    case 'terminada':
      return { tone: 'ok', icon: 'task_alt', title: 'Completada', message: contarRecibos(solicitud) };
    case 'expirada':
      return { tone: 'warn', icon: 'timer_off', title: 'Paquete vencido', message: enCastellano(solicitud.error_message) || 'El SAT solo mantiene el paquete 72 horas.' };
    case 'cancelada':
      return { tone: 'info', icon: 'cancel', title: 'Cancelada', message: 'Se canceló antes de pedirla al SAT.' };
    case 'failed':
    case 'rejected':
    default:
      return { tone: 'danger', icon: 'error', title: 'No se pudo completar', message: enCastellano(solicitud.error_message) || 'La solicitud falló.' };
  }
};

const PhaseCard = ({ phase, state }) => {
  const palette = {
    done: { bg: 'var(--ok-soft)', bd: 'var(--ok)', fg: 'var(--ok)' },
    active: { bg: 'var(--brand)', bd: 'var(--brand)', fg: '#fff' },
    failed: { bg: 'var(--danger-soft)', bd: 'var(--danger)', fg: 'var(--danger)' },
    pending: { bg: 'var(--surface-2)', bd: 'var(--line-2)', fg: 'var(--muted)' },
  }[state];

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 190px', minWidth: 0,
        padding: '14px 16px', borderRadius: 13, background: 'var(--surface-2)',
        border: `1px solid ${state === 'active' ? 'var(--brand-line)' : 'var(--line)'}`,
      }}
    >
      <span
        style={{
          width: 42, height: 42, borderRadius: 12, flexShrink: 0, display: 'grid', placeItems: 'center',
          background: palette.bg, border: `1px solid ${palette.bd}`, color: palette.fg,
        }}
      >
        {state === 'active' ? <Spinner size={20} style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,.4)' }} />
          : state === 'done' ? <Sym name="check" size={24} />
            : state === 'failed' ? <Sym name="close" size={24} />
              : <Sym name={phase.icon} size={22} />}
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="a2-mono" style={{ fontSize: '.64rem', fontWeight: 700, color: 'var(--muted)' }}>
          FASE {phase.num}
        </div>
        <div style={{ font: "700 1rem 'Hanken Grotesk'", color: 'var(--heading)' }}>{phase.title}</div>
        <div style={{ font: "500 .78rem/1.35 'Hanken Grotesk'", color: 'var(--muted)' }}>{phase.desc}</div>
      </div>
    </div>
  );
};

/**
 * Resumen de la ÚLTIMA sincronización cuando no hay ninguna en curso.
 *
 * Se separó del estado en vivo por un tropiezo real: al entrar a la pantalla sin
 * nada corriendo, la última —que podía ser una fallida de hace cinco días— se
 * pintaba con las fases en rojo y un banner de error, como si acabara de
 * romperse algo. El pasado se cuenta como pasado.
 */
const UltimaSincronizacion = ({ solicitud }) => {
  const info = resumen(solicitud);
  const fallo = ['failed', 'rejected', 'expirada'].includes(solicitud?.status);

  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px',
        borderRadius: 13, background: 'var(--surface-2)', border: '1px solid var(--line)',
      }}
    >
      <Sym
        name={info.icon}
        size={20}
        style={{ color: fallo ? 'var(--muted)' : 'var(--ok)', flexShrink: 0, marginTop: 2 }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ font: "700 .92rem 'Hanken Grotesk'", color: 'var(--heading)' }}>
          Última sincronización · {info.title}
        </div>
        <div className="a2-tcell-muted" style={{ marginTop: 2 }}>
          <span className="a2-mono">{periodoDe(solicitud)}</span>
          {solicitud?.started_at ? ` · solicitada el ${fechaHora(solicitud.started_at, { mesLargo: true })}` : ''}
        </div>
        {solicitud?.status === 'terminada' ? (
          <div className="a2-tcell-muted" style={{ marginTop: 2 }}>
            {contarRecibos(solicitud)}
          </div>
        ) : fallo && solicitud?.error_message ? (
          <div className="a2-tcell-muted" style={{ marginTop: 2 }}>
            {enCastellano(solicitud.error_message)}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * @param {boolean} vivo         la solicitud está corriendo ahora
 * @param {boolean} siempreFases pintar las fases aunque no esté viva. Se usa en
 *   el DETALLE de una sincronización concreta: ahí sí se está mirando ese
 *   proceso, mientras que en la pantalla del SAT lo que se quiere es empezar uno
 *   nuevo y solo recordar cómo acabó el anterior.
 */
const SyncStatusCard = ({ solicitud, vivo = false, siempreFases = false }) => {
  // Sin nada en curso no se pintan fases ni banners de error: eso describiría un
  // proceso que no está ocurriendo. Se invita a empezar y se recuerda cómo acabó
  // la anterior, que es la pregunta real de quien vuelve a esta pantalla.
  if (!vivo && !siempreFases) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <Note tone="info" icon="cloud_download">
          <span>
            <strong style={{ color: 'var(--heading)' }}>Listo para sincronizar.</strong>{' '}
            Elige el periodo y PaySlip lo pedirá al SAT. El proceso corre en el servidor.
          </span>
        </Note>
        {solicitud ? <UltimaSincronizacion solicitud={solicitud} /> : null}
      </div>
    );
  }

  const estados = fasesSegun(solicitud?.status);
  const info = resumen(solicitud);

  return (
    <div>
      <Progress
        value={AVANCE[solicitud?.status] ?? 0}
        label="Avance de la sincronización con el SAT"
        style={{ marginBottom: 20 }}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {FASES.map((f) => <PhaseCard key={f.id} phase={f} state={estados[f.id]} />)}
      </div>

      <div style={{ marginTop: 16 }}>
        <Note tone={info.tone} icon={info.icon}>
          <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <strong style={{ color: 'var(--heading)' }}>{info.title}.</strong>
            {info.message}
            {info.spinner ? <Spinner size={15} /> : null}
            {vivo ? <Badge tone="brand">El servidor la está llevando</Badge> : null}
          </span>
        </Note>
      </div>
    </div>
  );
};

export default SyncStatusCard;
